import React, { useEffect, useState, useCallback } from 'react';
import { Award, Trophy, Target, TrendingUp, BookOpen, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const scoreColor = (score) => {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-amber-400';
  return 'text-red-400';
};

const scoreBg = (score) => {
  if (score >= 80) return 'bg-green-900/30 border-green-700/40';
  if (score >= 60) return 'bg-amber-900/30 border-amber-700/40';
  return 'bg-red-900/30 border-red-700/40';
};

const StatMini = ({ icon: Icon, label, value, color = 'text-white' }) => (
  <div className="p-3 bg-slate-700/30 rounded-lg">
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className="w-3.5 h-3.5 text-gray-400" />
      <p className="text-xs text-gray-400">{label}</p>
    </div>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
  </div>
);

const QuizHistory = () => {
  const [attempts, setAttempts] = useState([]);
  const [stats, setStats] = useState({ total_attempts: 0, unique_content: 0, average_score: 0, best_score: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/my-quiz-history`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load history');
      setAttempts(data.attempts || []);
      setStats(data.stats || stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700 md:col-span-2" data-testid="quiz-history">
        <CardContent className="p-6 flex items-center gap-3 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading quiz history…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-slate-700 md:col-span-2" data-testid="quiz-history">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Award className="w-5 h-5 text-green-400" />
          Quiz History
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <p className="text-red-400 text-sm" data-testid="quiz-history-error">
            {error}
          </p>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatMini icon={Target} label="Attempts" value={stats.total_attempts} />
          <StatMini icon={BookOpen} label="Books" value={stats.unique_content} color="text-indigo-400" />
          <StatMini icon={TrendingUp} label="Avg score" value={`${stats.average_score}%`} color="text-amber-400" />
          <StatMini icon={Trophy} label="Best" value={`${Math.round(stats.best_score)}%`} color="text-green-400" />
        </div>

        {/* Attempt list */}
        {attempts.length === 0 && !error ? (
          <div className="text-center py-8 text-gray-500" data-testid="quiz-history-empty">
            You haven't taken any quizzes yet. Open any book and tap "Take Quiz" to start.
          </div>
        ) : (
          <div className="space-y-2">
            {attempts.map((a, i) => {
              const date = a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '';
              const score = Math.round(a.score || 0);
              return (
                <div
                  key={`${a.quiz_id}-${i}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${scoreBg(score)}`}
                  data-testid={`quiz-history-row-${i}`}
                >
                  <div className={`w-12 h-12 rounded-lg bg-slate-900/50 flex items-center justify-center flex-shrink-0 ${scoreColor(score)}`}>
                    <span className="text-sm font-bold">{score}%</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">
                      {a.content_title || 'Untitled'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {a.correct_count} / {a.total_questions} correct · {date}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuizHistory;
